import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Routes from '../routes'

import {
  selectCompanyThemePrimaryColor,
  selectCompanyTheme,
} from '../store/selectors/customerProfile'
import { selectToasts } from '../store/selectors/toasts'
import { setThemeCssColorVar } from '../hooks/theme'

// State
import { useTheme, legacyThemes } from '@homebot/shared-ui-swashbuckle'

// Components
import Notification from '../Components/shared/Notification'
import { LOADING_THEME } from '../constants/theme'
import DisclaimerModal from './DisclaimerModal'
import ErrorModal from './ErrorModal'
import { Toasts } from '../Components/shared/Toasts'
import { fetchCurrentUser } from '../actions/currentUser'

interface HomebotProps {
  error: boolean
}

const Homebot = ({ error }: HomebotProps) => {
  const { theme, setTheme } = useTheme()

  const urlParams = new URLSearchParams(window.location.search)
  const optionalThemeFromQueryStringForLocalDev = urlParams.get('devTheme')
  const themeFromCustomerProfile = useSelector(selectCompanyTheme)
  const companyThemeFromApiResponse =
    optionalThemeFromQueryStringForLocalDev || themeFromCustomerProfile

  const themePrimaryColor = useSelector(selectCompanyThemePrimaryColor)
  const themeTilCompanyResponse =
    localStorage.getItem(LOADING_THEME) ?? 'hb_default'
  const toasts = useSelector(selectToasts)

  // Needed for the PURL preview
  // Initially load the previous loaded theme
  useEffect(() => {
    setTheme(legacyThemes[themeTilCompanyResponse])
    // Remove the hardcoded background that we use before react
    // loads and use the background color design token instead
    document.body.style.removeProperty('background-image')
  }, [setTheme, themeTilCompanyResponse])

  useEffect(() => {
    // Set company theme ONY if different from theme set in local storage - prevents re-renders
    if (companyThemeFromApiResponse && companyThemeFromApiResponse !== theme) {
      setTheme(legacyThemes[companyThemeFromApiResponse])
      document.body.style.removeProperty('background-image')
      localStorage.setItem('LOADING_THEME', companyThemeFromApiResponse)
    }
  }, [setTheme, companyThemeFromApiResponse, theme.name])

  useEffect(() => {
    if (themePrimaryColor) {
      setThemeCssColorVar('brand-primary-custom', themePrimaryColor)
    }

    return () => {
      document.documentElement.style.removeProperty(`--brand-primary-custom`)
    }
  }, [themePrimaryColor])

  return (
    <div>
      <Notification />
      <Routes />
      <DisclaimerModal />
      <Toasts toasts={toasts} />
      {error && <ErrorModal />}
    </div>
  )
}

interface AppProps {
  error: boolean
}

export const App = (props: AppProps) => {
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(fetchCurrentUser())
  }, [dispatch])

  // Using design token theme provider
  return <Homebot error={props.error} />
}
